import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NoImg from "../assets/images/no-image-icon-23485.png";
import { MapPinIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";
import starGreen from "../assets/icons/StarGreen.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface PlaceCardProps {
  place: {
    id: string;
    name: string;
    rate: number;
    image_urls?: string[];
    address?: string;
    opening_hours?: string;
    description?: string;
    review_count: number;
  };
}

const fetchImageFileUrls = async (placeId: string): Promise<string[]> => {
  try {
    const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
    if (!res.ok) throw new Error("Failed to fetch images");
    const data = await res.json();
    return (data.data.images || []).map((img: any) => img.file_url);
  } catch {
    return [];
  }
};

const ImageWithBlurPreview: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
      {/* Blurred version of the same image — loads fast, shown while full res loads */}
      <img
        src={src}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl brightness-90"
      />
      {/* Full-res image fades in on top */}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = NoImg;
          setLoaded(true);
        }}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:brightness-75 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

const PlaceCard: React.FC<PlaceCardProps> = ({ place }) => {
  const [images, setImages] = useState<string[]>([]);
  const [imagesLoading, setImagesLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      const urls = await fetchImageFileUrls(place.id);
      if (isMounted) {
        setImages(urls);
        setImagesLoading(false);
      }
    };
    loadImages();
    return () => { isMounted = false; };
  }, [place.id]);

  const getImageUrl = (fileUrl?: string) => {
    if (!fileUrl) return NoImg;
    if (fileUrl.startsWith("http")) return fileUrl;
    return `${BASE_URL}${fileUrl}`;
  };

  const firstImage = images[0];

  return (
    <Link
      to={`/places/${place.id}`}
      className="group rounded-lg overflow-hidden bg-white border border-gray hover:shadow-md transition w-64 flex-shrink-0"
    >
      {/* Image with blur preview */}
      {imagesLoading ? (
        <div className="w-full h-48 bg-gray-200 animate-pulse rounded-t-lg" />
      ) : (
        <ImageWithBlurPreview src={getImageUrl(firstImage)} alt={place.name} />
      )}

      {/* Content */}
      <div className="p-3 text-black font-myYekanFaNumRegular">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-myYekanDemibold line-clamp-1 font-myYekanFaNumDemiBold">
            {place.name}
          </h3>
          <div className="flex justify-between items-center gap-2">
            {place.review_count > 0 && (
              <div className="text-xs text-gray-500">
                ({place.review_count} نظر)
              </div>
            )}
            {place.rate > 0 && (
              <div className="flex items-center gap-1 text-[#1BA75E] text-sm">
                {parseFloat(place.rate.toFixed(1))}
                <img src={starGreen} className="w-3 h-3 mb-1" alt="rating" />
              </div>
            )}
          </div>
        </div>
        {place.address && (
          <div className="flex items-start text-[11px] mt-2 text-gray-800">
            <MapPinIcon className="h-3 w-3 ml-1 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{place.address}</span>
          </div>
        )}
        {place.description && (
          <div className="text-[10px] text-gray-500 mt-2 line-clamp-2 text-justify font-myYekanFaNumRegular">
            <ReactMarkdown>{place.description}</ReactMarkdown>
          </div>
        )}
      </div>
    </Link>
  );
};

export default PlaceCard;